import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Clock, RefreshCw, CheckCircle2, XCircle, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const STATUS_STYLES = {
  active: { bg: "#F0FDF4", border: "#BBF7D0", color: "#15803D", label: "RUNNING" },
  disabled: { bg: "#F8FAFC", border: "#E2E8F0", color: "#94A3B8", label: "DISABLED" },
  error: { bg: "#FEF2F2", border: "#FECACA", color: "#DC2626", label: "ERROR" },
};

export default function SchedulerDashboardPage({ onLogout, userRole, isFeatureEnabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/scheduler-status`, { headers: getAuthHeaders() });
      setData(resp.data);
    } catch { toast.error("Failed to load scheduler status"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); const i = setInterval(fetchStatus, 30000); return () => clearInterval(i); }, [fetchStatus]);

  const formatAgo = (isoStr) => {
    if (!isoStr) return "Never";
    const diff = (new Date() - new Date(isoStr)) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading || !data) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading scheduler status...</div></Layout>;
  }

  const activeCount = data.schedulers.filter(s => s.status === "active").length;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="scheduler-dashboard">
        <PageHeader title="Background Schedulers" subtitle={`${activeCount} of ${data.schedulers.length} schedulers active`} testId="scheduler-title">
          <Button onClick={fetchStatus} variant="outline" className="rounded-none border-[#E2E8F0]" data-testid="refresh-schedulers">
            <RefreshCw size={14} className="mr-2" />Refresh
          </Button>
        </PageHeader>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] p-4 text-center">
            <div className="text-3xl font-mono font-black text-[#15803D]" data-testid="active-count">{activeCount}</div>
            <div className="text-xs text-[#15803D] font-bold uppercase tracking-wider mt-1">Active</div>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-center">
            <div className="text-3xl font-mono font-black text-[#94A3B8]" data-testid="disabled-count">{data.schedulers.filter(s => s.status === "disabled").length}</div>
            <div className="text-xs text-[#94A3B8] font-bold uppercase tracking-wider mt-1">Disabled</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-4 text-center">
            <div className="text-3xl font-mono font-black text-[#0F172A]" data-testid="total-count">{data.schedulers.length}</div>
            <div className="text-xs text-[#64748B] font-bold uppercase tracking-wider mt-1">Total</div>
          </div>
        </div>

        {/* Scheduler cards */}
        <div className="space-y-3">
          {data.schedulers.map(s => {
            const style = STATUS_STYLES[s.status] || STATUS_STYLES.disabled;
            return (
              <div
                key={s.key}
                className="flex items-center justify-between p-5 bg-white border transition-colors hover:border-[#0F172A]"
                style={{ borderColor: style.border }}
                data-testid={`scheduler-${s.key}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: style.bg }}>
                    {s.status === "active" ? <Activity size={20} style={{ color: style.color }} /> : <XCircle size={20} style={{ color: style.color }} />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-[#0F172A]">{s.name}</h3>
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}>
                        {style.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-[#64748B] flex items-center gap-1">
                        <Clock size={12} /> {s.interval}
                      </span>
                      {s.system && <span className="text-xs text-[#64748B]">System: <strong>{s.system}</strong></span>}
                      {s.tags_monitored !== undefined && <span className="text-xs text-[#64748B]">{s.tags_monitored} tag(s)</span>}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-mono font-bold" style={{ color: s.last_run ? "#0F172A" : "#94A3B8" }} data-testid={`last-run-${s.key}`}>
                    {formatAgo(s.last_run)}
                  </div>
                  <div className="text-[10px] text-[#94A3B8] font-mono mt-0.5">
                    {s.last_run ? new Date(s.last_run).toLocaleString() : "No runs yet"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center text-xs text-[#94A3B8] font-mono pt-2">
          Server time: {new Date(data.server_time).toLocaleString()} · Auto-refreshes every 30s
        </div>
      </div>
    </Layout>
  );
}
